import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDemo } from "@/context/DemoContext";
import ChangePasswordGate from "@/components/ChangePasswordGate";
import api from "@/lib/api";
import { dateIt } from "@/lib/format";
import {
  LayoutDashboard,
  Database,
  KanbanSquare,
  Map as MapIcon,
  Users,
  RadioTower,
  Settings as SettingsIcon,
  Bell,
  LogOut,
  X,
  Wallet as WalletIcon,
  ShieldCheck,
  FolderLock,
  FlaskConical,
  BookOpen,
  Menu,
  Gavel,
  Star,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/operazioni", label: "Operazioni", icon: Database, testid: "nav-operazioni" },
  { to: "/mappa", label: "Mappa", icon: MapIcon, testid: "nav-mappa" },
  { to: "/finder-aste", label: "Finder Aste", icon: Gavel, testid: "nav-finder-aste" },
  { to: "/finder-aste/preferiti", label: "Le mie operazioni", icon: Star, testid: "nav-le-mie-operazioni" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
];

// Account cliente (agenzie esterne): accesso limitato a Finder Aste + Le mie operazioni,
// nessun'altra tab (nemmeno Data Room/Manuale, a differenza dell'investitore).
const ASTE_ONLY_NAV = [
  { to: "/finder-aste", label: "Finder Aste", icon: Gavel, testid: "nav-finder-aste" },
  { to: "/finder-aste/preferiti", label: "Le mie operazioni", icon: Star, testid: "nav-le-mie-operazioni" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { showDemo, setShowDemo } = useDemo();
  const isAsteOnly = user?.role === "cliente_aste";
  const isInternal = user?.role !== "investitore" && !isAsteOnly;
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [open, setOpen] = useState(false);
  const [demoCount, setDemoCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Chiudi il menu mobile ad ogni cambio pagina
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const isAdmin = user?.role === "admin";
  const isAnalyst = user?.role === "analista";
  const navItems = isAsteOnly ? ASTE_ONLY_NAV : [
    ...(isInternal ? NAV : []),
    ...((isAdmin || isAnalyst) ? [{ to: "/fonti", label: "Fonti & Ingestion", icon: RadioTower, testid: "nav-fonti" }] : []),
    ...(isAdmin ? [{ to: "/operatori", label: "Operatori", icon: Users, testid: "nav-operatori" }] : []),
    { to: "/dataroom", label: "Data Room", icon: FolderLock, testid: "nav-dataroom" },
    { to: "/manuale", label: "Manuale", icon: BookOpen, testid: "nav-manuale" },
    ...(isInternal ? [{ to: "/wallet", label: "Wallet", icon: WalletIcon, testid: "nav-wallet" }] : []),
    ...(isAdmin ? [{ to: "/impostazioni", label: "Configurazione", icon: SettingsIcon, testid: "nav-impostazioni" }] : []),
    ...(isAdmin ? [{ to: "/amministrazione", label: "Amministrazione", icon: ShieldCheck, testid: "nav-amministrazione" }] : []),
  ];
  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
      isActive
        ? "bg-[#EA7B2B]/10 text-[#EA7B2B] border border-[#EA7B2B]/20"
        : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
    }`;

  useEffect(() => {
    if (isInternal) api.get("/demo/status").then((r) => setDemoCount(r.data.demo_count || 0)).catch(() => {});
  }, [isInternal]);

  // Cliente Finder Aste: accesso consentito solo a Finder Aste + Le mie operazioni.
  // Investitore: accesso consentito solo alla Data Room (e al Manuale).
  useEffect(() => {
    if (isAsteOnly) {
      if (!location.pathname.startsWith("/finder-aste")) navigate("/finder-aste", { replace: true });
      return;
    }
    if (!isInternal && !location.pathname.startsWith("/dataroom") && location.pathname !== "/manuale") {
      navigate("/dataroom", { replace: true });
    }
  }, [isInternal, isAsteOnly, location.pathname, navigate]);

  const loadNotifs = () => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    if (isInternal) api.get("/reminders").then((r) => setReminders(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.letta).length;
  const overdue = reminders.filter((r) => r.scaduto).length;
  const badge = unread + overdue;
  const markRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    loadNotifs();
  };

  return (
    <div className="min-h-screen flex bg-[#0A1826]">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`w-64 shrink-0 border-r border-white/10 bg-[#0C1F30] flex flex-col fixed h-screen z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-4 h-20 flex items-center justify-between border-b border-white/10">
          <Link to="/" className="flex flex-col justify-center gap-1.5" data-testid="brand-logo">
            <img src="/rebreton-logo-bianco.webp" alt="Rebreton Real Estate" className="h-9 w-auto object-contain object-left" />
            <span className="text-[10px] text-[#EA7B2B] uppercase tracking-[0.2em] pl-0.5">Beta Aste</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-400 hover:text-white p-1" data-testid="sidebar-close" aria-label="Chiudi menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid} className={linkCls}>
              <n.icon className="w-[18px] h-[18px] shrink-0" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm text-white font-medium truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.role_label}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-zinc-400 hover:text-[#E11D48] hover:bg-[#E11D48]/10 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Banner ambiente di test */}
        <div data-testid="test-env-banner" className="bg-[#EA7B2B] text-black text-[10px] sm:text-xs font-medium px-4 sm:px-8 py-1.5 flex items-center justify-center gap-2 text-center">
          <FlaskConical className="w-3.5 h-3.5 shrink-0" />
          <span>Ambiente di beta test{demoCount > 0 ? " · dati demo" : ""} · pagamenti Stripe in modalità test · servizi OpenAPI non attivi</span>
        </div>
        <header className="h-16 sm:h-20 sticky top-0 z-20 bg-[#0A1826]/80 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 sm:px-8 gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-toggle"
            aria-label="Apri menu"
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-sm border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 ml-auto">
          {isInternal && demoCount > 0 && (
            <button
              onClick={() => setShowDemo(!showDemo)}
              data-testid="toggle-demo-btn"
              title="Mostra/nascondi le operazioni demo"
              className={`hidden sm:flex items-center gap-2 h-10 px-3 rounded-sm border text-xs font-medium transition-colors ${showDemo ? "border-[#EA7B2B]/50 text-[#EA7B2B] bg-[#EA7B2B]/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
            >
              <FlaskConical className="w-4 h-4" />
              {showDemo ? "Dati demo: ON" : "Dati demo: OFF"}
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => {
                setOpen((o) => !o);
                if (!open && unread) markRead();
              }}
              data-testid="notifications-btn"
              className="relative w-10 h-10 flex items-center justify-center rounded-sm border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors"
            >
              <Bell className="w-5 h-5" />
              {badge > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#EA7B2B] text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                  {badge}
                </span>
              )}
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] max-w-96 max-h-[70vh] overflow-y-auto bg-[#122A3F] border border-white/10 rounded-sm shadow-2xl" data-testid="notifications-panel">
                {reminders.length > 0 && (
                  <>
                    <div className="px-4 py-2.5 border-b border-white/10 sticky top-0 bg-[#122A3F] flex items-center justify-between">
                      <span className="font-display text-sm text-white">Promemoria agenda</span>
                      <span className="text-[10px] text-[#EA7B2B] uppercase tracking-wider">{overdue} scaduti</span>
                    </div>
                    {reminders.slice(0, 12).map((r, i) => (
                      <button key={`rem-${i}`} onClick={() => { navigate(`/operazioni/${r.deal_id}`); setOpen(false); }}
                        data-testid={`reminder-${i}`}
                        className="w-full text-left px-4 py-2.5 border-b border-white/5 hover:bg-white/5 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: r.scaduto ? "#E11D48" : "#F59E0B" }} />
                          <span className="text-sm text-white flex-1">{r.testo}</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5 pl-3.5">{r.comune} · {r.data}{r.operatore ? ` · ${r.operatore}` : ""}{r.scaduto ? " · scaduto" : ""}</div>
                      </button>
                    ))}
                  </>
                )}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#122A3F]">
                  <span className="font-display text-sm text-white">Notifiche</span>
                  <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-zinc-500" /></button>
                </div>
                {notifs.length === 0 && <div className="p-6 text-sm text-zinc-500 text-center">Nessuna notifica</div>}
                {notifs.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { navigate(`/operazioni/${n.deal_id}`); setOpen(false); }}
                    className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white">{n.messaggio}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{n.titolo} · {n.comune} · {dateIt(n.created_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="w-10 h-10 rounded-sm bg-[#EA7B2B]/15 border border-[#EA7B2B]/30 flex items-center justify-center text-[#EA7B2B] font-display font-semibold shrink-0">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      <ChangePasswordGate />
    </div>
  );
}
